import { HttpClient } from '@angular/common/http';
import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, UrlSegment } from '@angular/router';
import { PayPeriod } from '@app/interfaces';
import { EPTReport } from '@app/modules/reporting/reports/enhanced-reports/ept-by-jurisdiction';
import { AuthService, NotifyService } from '@app/services';
import { FilterPayrollReportForm } from '@forms/reporting/components';
import { Schedule } from '@models/payroll/schedule.model';
import { PayrollResources } from '@payroll/payroll.resources';
import { ReportFilter } from '@reporting/interfaces/report-filter.interface';
import { EnhancedPayrollReport } from '@reporting/interfaces/report.interface';
import { ENHANCED_REPORTS_MAP } from '@reporting/reports/enhanced-reports';
import { EnhancedPayrollReportingService } from '@reporting/services';
import { Report } from '@reporting/services/queries/report';
import { isWithinRange, parse } from 'date-fns';
import { takeRightWhile } from 'lodash-es';
import moment from 'moment';
import stickybits from 'stickybits';

@Component({
    templateUrl: './enhanced-payroll-report.template.html',
    styleUrls: ['./enhanced-payroll-report.style.scss'],
    providers: [EnhancedPayrollReportingService],
})
export class EnhancedPayrollReportView implements OnInit {
    @ViewChild('filterForm', { static: true }) filterForm!: FilterPayrollReportForm;

    startAt: Date = new Date();
    endAt: Date = new Date();
    isSingleDayRange = false;
    payPeriodsStartAt: Date | null = null;
    payPeriodsEndAt: Date | null = null;
    lastChequeDate: Date | null = null;
    payPeriodRange: string = '';

    isLoading = true;
    includePending = true;

    report: Report | null = null;
    reportInterface: EnhancedPayrollReport | null = null;
    stickyColumns: (string | number | undefined)[] | undefined = [];
    lastStickyColumn: string | number | undefined;
    hasMultipleExportOptions = false;
    schedule: Schedule | null = null;
    displayBackRoute = true;

    constructor(
        private activatedRoute: ActivatedRoute,
        private auth: AuthService,
        private http: HttpClient,
        private notify: NotifyService,
        private reportingService: EnhancedPayrollReportingService,
        private router: Router
    ) {}

    async ngOnInit(): Promise<void> {
        this.setReportInterface(this.activatedRoute.snapshot.url);

        if (!this.reportInterface) {
            this.router.navigateByUrl('/404');
            return;
        }

        this.hasMultipleExportOptions =
            [this.reportInterface.hasPDF, this.reportInterface.hasExcel, this.reportInterface.hasCSV].filter((x) => x)
                .length > 1;

        this.loadSchedule()
            .then(() => this.display())
            .catch((err) => {
                this.isLoading = false;
                this.notify.error(err.message || 'reporting.payroll.couldNotLoadReport');
            });
    }

    onFilter(dates: { startAt: Date; endAt: Date }, includePending: boolean): void {
        if (dates.startAt == this.startAt && dates.endAt == this.endAt && includePending == this.includePending) {
            return;
        }
        this.setDates(dates.startAt, dates.endAt);
        this.includePending = includePending;
        this.display();
    }

    exportExcel(): void {
        if (!this.report) {
            return;
        }
        this.isLoading = true;
        this.reportingService.exportExcel(this.report, this.getReportFilters()).finally(() => (this.isLoading = false));
    }

    exportPDF(): void {
        if (!this.report) {
            return;
        }
        this.isLoading = true;
        this.reportingService.exportPDF(this.report, this.getReportFilters()).finally(() => (this.isLoading = false));
    }

    exportCSV(): void {
        if (!this.report) {
            return;
        }
        this.isLoading = true;
        this.reportingService.exportCSV(this.report, this.getReportFilters()).finally(() => (this.isLoading = false));
    }

    trackByFn(index: number): number {
        return index;
    }

    protected sticky(): void {
        setTimeout(() => {
            stickybits('.left-sticky');
            stickybits('.top-sticky');
        });
    }

    private async display(): Promise<void> {
        if (!this.reportInterface) {
            return;
        }
        this.isLoading = true;

        try {
            this.report = await this.reportingService.display(
                this.reportInterface,
                this.getReportFilters(),
                this.getAdditionalParams()
            );
            this.stickyColumns = this.report.sheet?.columns
                .filter((column) => column.sticky)
                .map((column) => column.key);
            if (this.stickyColumns) {
                this.lastStickyColumn = this.stickyColumns[this.stickyColumns.length - 1];
            }
            this.sticky();
        } catch (err) {
            this.notify.error(err.message || 'reporting.payroll.loadedWithErrors');
        }

        this.isLoading = false;
    }

    /**
     * Find the report that matches the final url segment
     * We do not use the `report` param because it is possible
     * to be linked to this report and not have the `report` param set.
     */
    private setReportInterface(url: UrlSegment[]): void {
        const reportUrlLastSegment = takeRightWhile(url, (segment) => segment.path !== 'enhanced').map(
            (segment) => segment.path
        );
        this.reportInterface = ENHANCED_REPORTS_MAP[reportUrlLastSegment.join('/')];
    }

    private async loadSchedule(): Promise<void> {
        if (!this.reportInterface) {
            return;
        }
        try {
            const res = await this.http
                .get(PayrollResources.Schedule.replace(':company', this.auth.company.id.toString()))
                .toPromise();
            this.schedule = new Schedule(res['data']['attributes']);
            const [startAt, endAt] = this.reportInterface.getInitialDates(this.schedule);
            this.setDates(startAt, endAt);
        } catch (err) {
            this.notify.error(err.message || 'reporting.payroll.couldNotLoadSchedule');
            this.isLoading = false;
        }
    }

    private getReportFilters(): ReportFilter[] {
        if (this.reportInterface?.isAnnual) {
            return [{ name: 'year', value: moment(this.startAt).year() }];
        }

        return [
            { name: 'startAt', value: moment(this.startAt).format('YYYY-MM-DD') },
            { name: 'endAt', value: moment(this.endAt).format('YYYY-MM-DD') },
        ];
    }

    private getAdditionalParams(): [string, string][] | undefined {
        const additionalParams: [string, string][] = [];
        if (this.reportInterface && this.isEPTReport(this.reportInterface)) {
            additionalParams.push(['jurisdiction_area', this.reportInterface.jurisdiction]);
        }

        return additionalParams.length ? additionalParams : undefined;
    }

    private isEPTReport(report: EnhancedPayrollReport): report is EPTReport {
        return 'jurisdiction' in report;
    }

    private setDates(startAt: Date, endAt: Date): void {
        this.isSingleDayRange =
            moment(startAt).isSame(endAt, 'day') || moment(startAt).add(1, 'day').isSame(endAt, 'day');

        this.startAt = startAt;
        this.endAt = endAt;

        this.getPayPeriodDateRange();
    }

    private getPayPeriodDateRange(): void {
        if (!this.schedule) {
            return;
        }

        const year = parse(this.startAt).getFullYear();
        const beginning = new Date(year, 0, 1);
        const end = new Date(year, 11, 31);
        const selectedPayPeriods = this.schedule['allPeriods']
            .flatMap((yearItem) => {
                return yearItem.year >= parse(this.startAt).getFullYear() &&
                    yearItem.year <= parse(this.endAt).getFullYear()
                    ? yearItem.payPeriods
                    : [];
            })
            .filter(
                (period: PayPeriod) => period.type !== 'Stub' && isWithinRange(parse(period.chequeDate), beginning, end)
            );

        const firstPayPeriod = selectedPayPeriods.slice().at(0);
        const lastPayPeriod = selectedPayPeriods.slice().at(-1);

        this.payPeriodRange = firstPayPeriod.periodNo + ' - ' + lastPayPeriod.periodNo;
        this.payPeriodsStartAt = firstPayPeriod.chequeDate;
        this.payPeriodsEndAt = lastPayPeriod.chequeDate;
        this.lastChequeDate = lastPayPeriod.chequeDate;
    }
}
